import React from 'react';
import moment from 'moment';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Document, Page, View, Text, StyleSheet, pdf } from '@react-pdf/renderer';
import { getDocumentDownloadUrlApi } from 'Constants';
import { getCaseType } from 'Utils';
import { clientAnswers } from 'Components/Professional/Cases/CaseStage';

// ---------------------------------------------------------------------------
// Client's copy of a closed case: a PDF summary (case, attorney/funder, every
// answer the client gave, their uploads) plus the uploaded files themselves,
// bundled into one .zip.
// ---------------------------------------------------------------------------

const NAVY = '#14213d';
const BRASS = '#8a6231';
const MUTED = '#6b7280';

const s = StyleSheet.create({
  page: { paddingTop: 40, paddingBottom: 56, paddingHorizontal: 44, fontSize: 10.5, color: NAVY, fontFamily: 'Helvetica' },
  eyebrow: { fontSize: 8.5, letterSpacing: 1.2, color: BRASS, textTransform: 'uppercase', marginBottom: 6 },
  title: { fontSize: 20, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  sub: { fontSize: 10.5, color: MUTED, marginBottom: 18 },
  section: { marginTop: 16 },
  h2: { fontSize: 12, fontFamily: 'Helvetica-Bold', paddingBottom: 6, marginBottom: 4, borderBottomWidth: 1, borderBottomColor: '#d9d4c7' },
  row: { flexDirection: 'row', paddingVertical: 5, borderBottomWidth: 0.5, borderBottomColor: '#ebe6da' },
  label: { width: '46%', color: MUTED, paddingRight: 10 },
  value: { width: '54%', fontFamily: 'Helvetica-Bold' },
  person: { marginTop: 6, padding: 10, backgroundColor: '#f7f4ed', borderRadius: 4 },
  personName: { fontSize: 11.5, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
  small: { fontSize: 9.5, color: MUTED },
  footer: { position: 'absolute', bottom: 24, left: 44, right: 44, fontSize: 8.5, color: MUTED, flexDirection: 'row', justifyContent: 'space-between' },
});

const money = (v) => Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const estimateOf = (d) => {
  const lo = d?.aiEstimateLowerValue ?? d?.aiEstimate?.lowerValue;
  const hi = d?.aiEstimateUpperValue ?? d?.aiEstimate?.upperValue;
  if (lo == null || hi == null || isNaN(Number(lo)) || isNaN(Number(hi))) return null;
  return `${money(lo)} - ${money(hi)}`;
};
const fullName = (p) => `${p?.firstName || ''} ${p?.lastName || ''}`.trim();

function Row({ label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <View style={s.row} wrap={false}>
      <Text style={s.label}>{label}</Text>
      <Text style={s.value}>{String(value)}</Text>
    </View>
  );
}

function Person({ p, role }) {
  if (!p) return null;
  return (
    <View style={s.person} wrap={false}>
      <Text style={s.small}>{role}</Text>
      <Text style={s.personName}>{p.isUserFunder && p.company ? p.company : fullName(p)}</Text>
      {p.isUserFunder && fullName(p) ? <Text>{fullName(p)}</Text> : null}
      {!p.isUserFunder && p.company ? <Text>{p.company}</Text> : null}
      {p.email ? <Text>Email: {p.email}</Text> : null}
      {p.phoneNumber ? <Text>Phone: {p.phoneNumber}</Text> : null}
    </View>
  );
}

export function CaseSummaryDoc({ caseDetails, people = [], closedOn }) {
  const d = caseDetails || {};
  const answers = clientAnswers(d, true);
  const files = [...(d.supportingDocuments || []), ...(d.videoDocument || [])];
  return (
    <Document title={`${d.caseNumber || 'Case'} - case summary`}>
      <Page size="LETTER" style={s.page}>
        <Text style={s.eyebrow}>Quick Case Review · Case summary</Text>
        <Text style={s.title}>{d.caseNumber} · {getCaseType(d.caseType)}</Text>
        <Text style={s.sub}>{closedOn ? `Closed on ${moment(closedOn).format('MMMM D, YYYY')}` : 'Closed case'}</Text>

        {people.length > 0 &&
          <View style={s.section}>
            <Text style={s.h2}>Who you closed this case with</Text>
            {people.map((x, i) => <Person key={i} p={x.p} role={x.role} />)}
          </View>}

        <View style={s.section}>
          <Text style={s.h2}>Case overview</Text>
          <Row label="Case number" value={d.caseNumber} />
          <Row label="Case type" value={getCaseType(d.caseType)} />
          <Row label="Estimated value" value={estimateOf(d)} />
          <Row label="Difficulty" value={d.aiDifficulty ? `${d.aiDifficulty}/10` : null} />
          <Row label="Deadline to file (estimate)" value={d.aiDate ? moment(d.aiDate).format('MMMM D, YYYY') : null} />
          <Row label="Posted on" value={d.created_at ? moment(d.created_at).format('MMMM D, YYYY') : null} />
        </View>

        <View style={s.section}>
          <Text style={s.h2}>Your contact details</Text>
          <Row label="Name" value={fullName(d) && fullName(d) !== 'null' ? fullName(d) : null} />
          <Row label="Email" value={d.contactEmail} />
          <Row label="Phone" value={d.contactPhone} />
          <Row label="Preferred contact" value={d.contactPref} />
          <Row label="Location" value={[d.clientCity, d.clientState, d.clientZipcode].filter(Boolean).join(', ')} />
        </View>

        <View style={s.section}>
          <Text style={s.h2}>Your answers</Text>
          {answers.map((a) => <Row key={a.q} label={a.q} value={a.a} />)}
          <Row label="Adverse party" value={d.adversePartyName} />
        </View>

        <View style={s.section}>
          <Text style={s.h2}>Files you uploaded</Text>
          {files.length
            ? files.map((f, i) => <Text key={i} style={{ paddingVertical: 2 }}>{i + 1}. {f.fileName}</Text>)
            : <Text style={s.small}>No files were uploaded.</Text>}
          {files.length ? <Text style={[s.small, { marginTop: 4 }]}>The files are in the "documents" folder next to this PDF.</Text> : null}
        </View>

        <View style={s.footer} fixed>
          <Text>Generated {moment().format('MMM D, YYYY')} · Quick Case Review</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}

const safe = (name) => String(name || 'file').replace(/[\\/:*?"<>|]+/g, '_');

// Builds and saves "<CASE-ID> - case details.zip". onProgress(text) is optional.
export async function downloadCaseBundle({ caseDetails, people, closedOn, makeApiCallFunc, onProgress }) {
  const d = caseDetails || {};
  const zip = new JSZip();
  onProgress && onProgress('Preparing summary…');
  const summary = await pdf(<CaseSummaryDoc caseDetails={d} people={people} closedOn={closedOn} />).toBlob();
  zip.file(`${safe(d.caseNumber)} - case summary.pdf`, summary);

  const files = [
    ...(d.supportingDocuments || []).map((f) => ({ ...f, t: 'supportingDocuments' })),
    ...(d.videoDocument || []).map((f) => ({ ...f, t: 'videoDocument' })),
  ];
  let failed = 0;
  const used = {};
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    onProgress && onProgress(`Adding files (${i + 1} of ${files.length})…`);
    // signed, short-lived URL per file (uploads are private)
    const url = await new Promise((resolve) => makeApiCallFunc(
      getDocumentDownloadUrlApi,
      { caseId: d._id, documentTypeName: f.t, fileName: f.fileName, location: f.location },
      ({ data }) => resolve(data?.url || null),
      () => resolve(null),
      null, true,
    ));
    if (!url) { failed++; continue; }
    try {
      const blob = await (await fetch(url)).blob();
      let name = safe(f.fileName);
      if (used[name]) { const dot = name.lastIndexOf('.'); name = dot > 0 ? `${name.slice(0, dot)} (${used[name]})${name.slice(dot)}` : `${name} (${used[name]})`; }
      used[safe(f.fileName)] = (used[safe(f.fileName)] || 0) + 1;
      zip.file('documents/' + name, blob);
    } catch (e) { failed++; }
  }
  onProgress && onProgress('Zipping…');
  const out = await zip.generateAsync({ type: 'blob' });
  saveAs(out, `${safe(d.caseNumber)} - case details.zip`);
  return { files: files.length, failed };
}
